import { useId, useRef, useState, type DragEvent, type ReactNode } from "react";
import { UploadCloud } from "lucide-react";
import clsx from "clsx";
import { Icon } from "../Icon/Icon.js";
import styles from "./Dropzone.module.scss";

/**
 * Which surface the zone is sitting ON. `default` is the light card ground it was built for; `inverse` is a
 * DARK ground — the show preview's flyer tile paints the poster gradient behind it, and every one of the
 * default's colours (ink prompt, `t3` hint, `line-strong` border) is chosen against paper. Left unswapped
 * they render near-black on near-black: the prompt measured 1.2:1 and was effectively invisible, which is how
 * it shipped (reported 2026-08-26, *"drop flyer here text is illegible"*).
 *
 * `bare` is a LIGHT ground the zone should not paint over: no fill at rest, so whatever the surface is shows
 * through, and a hover that darkens it slightly instead of swapping it. The dashed border is the affordance;
 * a filled box on a tinted work surface reads as a panel that is already holding something.
 *
 * A TONE, not a caller-side override, because the pieces that need swapping are the component's own children
 * — a call site cannot reach `.prompt`/`.hint` through a CSS module, and reaching them by element selector
 * (`label span`) would break the moment this markup changes.
 */
export type DropzoneTone = "default" | "inverse" | "bare";

/**
 * How much room the zone takes. `default` stacks icon / prompt / hint down the middle (125px measured);
 * `compact` lays the same three out as a wrapping row at half that (64px) — ART-89, *"half of the height they
 * are"*, against a reference dropzone that is one centred line inside 22px of padding. A DENSITY rather than a
 * caller override for the same reason {@link DropzoneTone} is a tone: the pieces that have to change are this
 * component's own children.
 */
export type DropzoneDensity = "default" | "compact";

export interface DropzoneProps {
  /** Called with the first file (in drop order) that satisfies `accept`; files failing `accept` never reach here. */
  onFile: (file: File) => void;
  /** The `accept` filter — the native file-input filter AND the drop-time gate. Comma-separated mime types
   *  (`image/jpeg`), wildcards (`image/*`), or extensions (`.pdf`). Omit to accept any file. */
  accept?: string;
  /** Disable the whole surface (no browse, no drop, input out of the tab order). */
  disabled?: boolean;
  /** Accessible name for the file control (the visually-hidden input the label wraps). Default "Upload a file". */
  label?: string;
  /** Primary prompt line. Default "Drag a file here, or click to browse". */
  prompt?: ReactNode;
  /** Supporting hint under the prompt (e.g. "JPEG only"); programmatically described to the control. */
  hint?: ReactNode;
  /** Message shown (as a live-region alert) when a dropped/picked file fails `accept`. */
  rejectMessage?: string;
  /** The ground this zone sits on. See {@link DropzoneTone}. */
  tone?: DropzoneTone;
  /** How much room the zone takes. See {@link DropzoneDensity}. */
  density?: DropzoneDensity;
  /** Extra class on the surface. */
  className?: string;
}

/** Does `file` satisfy an `accept` string? Handles `mime/type`, `mime/*` wildcards, and `.ext` suffixes. */
function matchesAccept(file: File, accept?: string): boolean {
  if (!accept) return true;
  const name = file.name.toLowerCase();
  const mime = file.type.toLowerCase();
  return accept
    .split(",")
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean)
    .some((t) => {
      if (t.startsWith(".")) return name.endsWith(t);
      if (t.endsWith("/*")) return mime.startsWith(t.slice(0, -1));
      return mime === t;
    });
}

/**
 * `Dropzone` (Upload) — the shell-agnostic drag-drop + click-to-browse upload surface. It is a `<label>`
 * wrapping a visually-hidden (but focusable) file input, so click-anywhere and keyboard both open the picker
 * NATIVELY — no `role="button"` (whose subtree ARIA prunes, which would silence the reject alert and strip the
 * prompt/hint). It emits the first file that passes `accept` via `onFile` (the consumer owns the upload + its
 * error/progress states) and surfaces a live-region alert when a file fails `accept`. First consumer: Antenna's
 * verification-photo no-camera fallback.
 */
export function Dropzone({
  onFile,
  accept,
  disabled = false,
  label = "Upload a file",
  prompt = "Drag a file here, or click to browse",
  hint,
  rejectMessage = "That file type isn't supported.",
  tone = "default",
  density = "default",
  className,
}: DropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const hintId = useId();
  const rejectId = useId();
  const [dragging, setDragging] = useState(false);
  // A nonce, not a boolean: it increments per rejection so the alert re-keys and re-announces even on a second
  // identical reject (a no-op boolean would leave the DOM unchanged, silencing the live region). 0 = no reject.
  const [rejectNonce, setRejectNonce] = useState(0);
  const rejected = rejectNonce > 0;

  function emit(files: readonly File[] | FileList | null | undefined) {
    if (!files || files.length === 0) return;
    const match = Array.from(files).find((f) => matchesAccept(f, accept));
    if (!match) {
      setRejectNonce((n) => n + 1);
      return;
    }
    setRejectNonce(0);
    onFile(match);
  }

  function onDragOver(e: DragEvent<HTMLLabelElement>) {
    // preventDefault BEFORE the disabled bail so a drop on a disabled zone can't fall through to the browser
    // default (navigate to / open the dropped file, blowing away the SPA).
    e.preventDefault();
    if (disabled) return;
    if (!dragging) setDragging(true);
  }

  function onDragLeave(e: DragEvent<HTMLLabelElement>) {
    // Ignore leaves fired while crossing into a child element (relatedTarget still inside the surface).
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setDragging(false);
  }

  function onDrop(e: DragEvent<HTMLLabelElement>) {
    e.preventDefault();
    if (disabled) return;
    setDragging(false);
    emit(e.dataTransfer.files);
  }

  const describedBy = [hint != null && hint !== "" ? hintId : null, rejected ? rejectId : null]
    .filter(Boolean)
    .join(" ");

  return (
    <label
      className={clsx(styles.zone, className)}
      data-tone={tone === "default" ? undefined : tone}
      data-density={density === "default" ? undefined : density}
      data-dragging={dragging || undefined}
      data-disabled={disabled || undefined}
      onDragOver={onDragOver}
      onDragEnter={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        disabled={disabled}
        className={styles.input}
        aria-label={label}
        aria-describedby={describedBy || undefined}
        onChange={(e) => {
          // Snapshot to an ARRAY before clearing the value. `e.target.files` is a LIVE FileList and the reset
          // below empties it IN PLACE (Chrome hands back the same object each access), so capturing the
          // reference and emitting it AFTER `value = ""` passes an EMPTY list — click-to-browse then fires no
          // `onFile` at all, silently (no request, no reject, nothing). The clear is still needed so re-picking
          // the same file fires `change` again; it just has to come after the snapshot. Only the drop path was
          // ever tested (it reads `dataTransfer.files`, a different object), so this was invisible until a real
          // browser hit it — reported as travel-parse doing nothing on click-to-upload (ART-225, 2026-09-16).
          const files = Array.from(e.target.files ?? []);
          e.target.value = ""; // allow re-picking the same file
          emit(files);
        }}
      />
      {/* Sized with the box: 22px is a third of the compact zone's height, where it stops reading as a mark
          beside a line and starts being the loudest thing in it. */}
      <Icon icon={UploadCloud} size={density === "compact" ? 16 : 22} className={styles.icon} />
      <span className={styles.prompt}>{prompt}</span>
      {hint != null && hint !== "" && (
        <span id={hintId} className={styles.hint}>
          {hint}
        </span>
      )}
      {rejected && (
        <span key={rejectNonce} id={rejectId} className={styles.reject} role="alert">
          {rejectMessage}
        </span>
      )}
    </label>
  );
}
